import React, { useState, useEffect, useRef } from 'react';
import {
  Container,
  Grid,
  Button,
  Stack,
  rem,
  Box,
  Loader,
  Alert,
} from '@mantine/core';
import { useParams, Link, useNavigate, useSearchParams } from 'react-router-dom';
import {
  IconArrowLeft,
  IconInfoCircle,
} from '@tabler/icons-react';
import { useAuth } from '../context/AuthContext';
import { useCart } from '../context/CartContext';
import { useTranslation } from '../context/TranslationContext';
import apiService from '../services/api';
import ShareButtons from '../components/ShareButtons';
import ImageLightbox from '../components/ImageLightbox';
import ProductDetailImageGallery from '../components/ProductDetailImageGallery';
import ProductDetailInfo from '../components/ProductDetailInfo';
import ProductDetailPurchaseCard from '../components/ProductDetailPurchaseCard';
import { localizedField } from '../utils/localizedField';
import { effectiveCustomerPrice } from '../utils/adminCustomerPrice';
import { isVariantSoldOut } from '../utils/soldOut';
import './ProductDetail.css';

export default function ProductDetail() {
  const { t } = useTranslation();
  const { productCode } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();
  const { addToCart } = useCart();

  const [parentProduct, setParentProduct] = useState(null);
  const [variants, setVariants] = useState([]);
  const [selectedVariant, setSelectedVariant] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [quantity, setQuantity] = useState(1);
  const [addingToCart, setAddingToCart] = useState(false);
  const [addedToCart, setAddedToCart] = useState(false);
  const [selectedImageIndex, setSelectedImageIndex] = useState(0);
  const [lightboxOpened, setLightboxOpened] = useState(false);
  const [lightboxIndex, setLightboxIndex] = useState(0);
  const [mainHovered, setMainHovered] = useState(false);

  const cartMessageTimer = useRef(null);

  useEffect(() => {
    const fetchProduct = async () => {
      try {
        setLoading(true);
        const data = await apiService.getParentProduct(productCode, t.langCode);
        setParentProduct(data);
        const visibleVariants = (data.variants || []).filter(v => !v.is_hidden);
        setVariants(visibleVariants);
        const defaultVariant = visibleVariants.find(v => v.is_default) || visibleVariants[0];
        // Honor deep links to a specific variant (e.g. featured cards) when valid.
        const requestedSku = searchParams.get('variant');
        const initialVariant = requestedSku
          ? visibleVariants.find(v => v.sku === requestedSku) || defaultVariant
          : defaultVariant;
        setSelectedVariant(initialVariant);
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };
    fetchProduct();
    return () => clearTimeout(cartMessageTimer.current);
  }, [productCode, t.langCode]);

  const handleAddToCart = async () => {
    if (!isAuthenticated) return navigate('/login');
    if (!selectedVariant) return;
    try {
      setAddingToCart(true);
      await addToCart({ ...selectedVariant, name: parentProduct.name }, quantity);
      setAddingToCart(false);
      setAddedToCart(true);
      clearTimeout(cartMessageTimer.current);
      cartMessageTimer.current = setTimeout(() => setAddedToCart(false), 2000);
    } catch (error) {
      setAddingToCart(false);
    }
  };

  const handleVariantChange = (v) => {
    setSelectedVariant(v);
    setQuantity(1);
    setSelectedImageIndex(0);
  };

  if (loading) return <Container py={100} ta="center"><Loader size="xl" variant="dots" /></Container>;

  if (error || !parentProduct) {
    return (
      <Container py={100}>
        <Alert icon={<IconInfoCircle size={16} />} title="Product Not Found" color="red" radius="md">
          {error || t('productNotFoundText', 'The product you are looking for does not exist.')}
        </Alert>
        <Button variant="light" leftSection={<IconArrowLeft size={16} />} onClick={() => navigate('/products')} mt="md">
          {t('backToProducts')}
        </Button>
      </Container>
    );
  }

  const isSoldOut = !selectedVariant || isVariantSoldOut(selectedVariant);
  const product = parentProduct;
  const productName = localizedField(product, 'name', t.langCode);
  const storePrice = selectedVariant?.price ?? null;
  const retailPrice = selectedVariant?.originalPrice ?? storePrice;
  const customerPrice = selectedVariant?.customerPrice || null;
  const effectivePrice = customerPrice
    ? effectiveCustomerPrice(selectedVariant, customerPrice, quantity)
    : null;
  const hasEffectivePrice = effectivePrice != null && Number.isFinite(Number(effectivePrice));
  const price = hasEffectivePrice ? Number(effectivePrice) : storePrice;
  const hasSpecialPrice = hasEffectivePrice
    ? Math.abs(Number(price) - Number(retailPrice)) >= 0.005
    : Boolean(selectedVariant?.hasSpecialPrice);
  const originalPrice = hasSpecialPrice ? retailPrice : null;
  // Prices are exposed GROSS; business users (vatExclusiveDisplay) swap to NET.
  const vatExclusive = Boolean(selectedVariant?.vatExclusiveDisplay);
  const hasNetPrice = selectedVariant?.netPrice != null;
  const displayPrice = price != null
    ? (vatExclusive
        ? (hasEffectivePrice ? Number(effectivePrice) : hasNetPrice ? Number(selectedVariant.netPrice) : Number(price) / (1 + Number(selectedVariant?.vatRate || 0) / 100))
        : Number(price))
    : null;
  const displayOriginal = originalPrice != null
    ? (vatExclusive ? Number(originalPrice) / (1 + Number(selectedVariant?.vatRate || 0) / 100) : Number(originalPrice))
    : null;
  const discountPct = selectedVariant?.discountPercentage ?? (hasSpecialPrice && displayOriginal && displayPrice
    ? Math.round(((displayOriginal - displayPrice) / displayOriginal) * 100) : null);
  const stockQuantity = selectedVariant?.stock_quantity ?? 0;

  // Collect and filter images by language priority
  const collectImages = (allImagesArr) => {
    if (!allImagesArr || !Array.isArray(allImagesArr)) return [];
    return allImagesArr;
  };

  const variantAllImages = collectImages(selectedVariant?.allImages);
  const parentAllImages = collectImages(product.allImages);
  const currentLang = t.langCode;

  const filterByLang = (imgs) => {
    const langMatch = [];
    const common = [];
    const otherLang = [];
    for (const img of imgs) {
      if (!img.lang) {
        common.push(img.url);
      } else if (img.lang === currentLang) {
        langMatch.push(img.url);
      } else {
        otherLang.push(img.url);
      }
    }
    return { langMatch, common, otherLang };
  };

  const vFiltered = filterByLang(variantAllImages);
  const pFiltered = filterByLang(parentAllImages);

  const allImages = [
    ...pFiltered.common,
    ...pFiltered.langMatch,
    ...vFiltered.common,
    ...vFiltered.langMatch,
  ];

  // Deduplicate
  const seenImages = new Set();
  const displayImages = allImages.filter(img => {
    if (seenImages.has(img)) return false;
    seenImages.add(img);
    return true;
  });

  return (
    <Box pb={rem(100)}>
      <Container size="lg" pt={rem(40)}>
        <Button
          component={Link}
          to="/products"
          variant="subtle"
          color="gray"
          leftSection={<IconArrowLeft size={18} />}
          mb="xl"
        >
          {t('backToProducts')}
        </Button>

        <Grid gutter={rem(80)}>
          {/* IMAGE COLUMN */}
          <Grid.Col span={{ base: 12, md: 6 }}>
            <ProductDetailImageGallery
              displayImages={displayImages}
              selectedImageIndex={selectedImageIndex}
              onSelectImage={setSelectedImageIndex}
              productName={productName}
              isSoldOut={isSoldOut}
              onOpenLightbox={() => { setLightboxIndex(selectedImageIndex); setLightboxOpened(true); }}
              hovered={mainHovered}
              onHoverChange={setMainHovered}
            />
          </Grid.Col>

          {/* INFO COLUMN */}
          <Grid.Col span={{ base: 12, md: 6 }}>
            <Stack gap="xl">
              <ProductDetailInfo
                product={product}
                selectedVariant={selectedVariant}
                productName={productName}
                displayPrice={displayPrice}
                displayOriginal={displayOriginal}
                hasSpecialPrice={hasSpecialPrice}
                vatExclusive={vatExclusive}
                discountPct={discountPct}
                isSoldOut={isSoldOut}
                t={t}
              />

              <ProductDetailPurchaseCard
                product={product}
                variants={variants}
                selectedVariant={selectedVariant}
                quantity={quantity}
                onQuantityChange={setQuantity}
                stockQuantity={stockQuantity}
                isSoldOut={isSoldOut}
                onAddToCart={handleAddToCart}
                addingToCart={addingToCart}
                addedToCart={addedToCart}
                onBuyNow={() => navigate('/checkout/confirm', { state: { variantSku: selectedVariant.sku, quantity } })}
                onVariantChange={handleVariantChange}
                t={t}
              />

              <Box className="product-description-content"
                dangerouslySetInnerHTML={{ __html: localizedField(product, 'description', t.langCode) + (selectedVariant?.article_number ? `<h3>${t('articleNumber', 'Article Number')}</h3><p>${selectedVariant.article_number}</p>` : '') }}
              />

              <Box>
                <ShareButtons
                  url={window.location.href}
                  title={productName}
                />
              </Box>
            </Stack>
          </Grid.Col>
        </Grid>
      </Container>

      <ImageLightbox
        opened={lightboxOpened}
        onClose={() => setLightboxOpened(false)}
        images={displayImages.map((url) => ({ src: url, alt: productName }))}
        selectedIndex={lightboxIndex}
        onSelectIndex={setLightboxIndex}
      />
    </Box>
  );
}